import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { isFreeMovie, showName } from '../../../shared/utils/playerUtils';
import { ClevertapEvent } from '../enum/clevertap.eventType';
import { VODInfo } from '../models/clevertap.model';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { AhaUserType } from 'src/app/shared/utils/SubscriptionUtil';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import { LanguageService } from 'src/app/language.service';
import { DataService } from 'src/app/data.service';

@Injectable({
  providedIn: 'root',
})
export class ClevertapContentStore {
  private currentResource: ResourceVm | undefined;

  constructor(
    private readonly languageService: LanguageService,
    private readonly sessionStorage: SessionStorageService,
    private readonly dataService: DataService
  ) {}

  get resource(): ResourceVm | undefined {
    return this.currentResource;
  }

  public setContentDetails(resource: ResourceVm | undefined) {
    this.currentResource = resource;
  }

  public getContentDetails(): ResourceVm | undefined {
    return this.currentResource;
  }

  public getContentInfoByEvent(eventName: ClevertapEvent): any {
    if (!eventName) return {};

    switch (eventName) {
      case ClevertapEvent.SKIP_INTRO:
        return this.skipIntroContent();
      case ClevertapEvent.WATCHED:
        return this.watchedContent();
      case ClevertapEvent.ADD_WATCHLIST:
      case ClevertapEvent.CONTENT_CLICKED:
        return this.addToWatchlistAndContentClicked();
      case ClevertapEvent.REMOVE_WATCHLIST:
        return this.removeFromWatchlistContent();
      case ClevertapEvent.CLICKED_WATCH_ON_RENT:
        return this.clickedWatchOnRent();
      default: // avoid resource content data if the event is not present in the above list
        return {};
    }
  }

  private getCastInfo(cast: any): any {
    let castInfo: any[] = [];
    if (cast && cast.length > 0) {
      cast.forEach((actor: any) => {
        const localizedCast =
          actor.localized &&
          actor.localized.filter((locs: any) => locs.languageCode === 'en');
        if (localizedCast) {
          castInfo = [...castInfo, localizedCast[0].localizeName];
        }
      });
    }
    return castInfo.length > 0 ? castInfo.join(',') : undefined;
  }

  public getTrayContentData(): any {
    let trayName: string | undefined = undefined;
    if (this.currentResource?.containerName) {
      trayName =
        this.currentResource.containerName[this.languageService.currLang] ||
        undefined;
    } else if (this.dataService.currentResourceContainerName) {
      trayName = this.dataService.currentResourceContainerName;
    }
    let positionInTray: string | number | undefined = undefined;
    if (
      this.currentResource?.positionInTray !== undefined &&
      this.currentResource?.positionInTray !== null
    ) {
      positionInTray = this.currentResource.positionInTray;
    } else if (
      this.dataService.currentResourcePositionInTray !== null &&
      this.dataService.currentResourcePositionInTray !== undefined
    ) {
      positionInTray = this.dataService.currentResourcePositionInTray;
    }
    return {
      trayName,
      positionInTray,
      trayType: this.currentResource?.containerType,
    };
  }

  private getDefaultContentInfo(): any {
    const currentLang = this.languageService.currLang;
    const attributes: any = {
      contentID: this.currentResource?.id,
      contentTitle: this.currentResource?.title[currentLang],
      contentGenre: this.currentResource?.contentGenre?.[currentLang]
        ? this.currentResource?.contentGenre?.[currentLang].join(' , ')
        : undefined,
      contentType: this.currentResource?.contentType,
      //networkType: this.currentResource?.network,
      contentDuration: this.currentResource?.runningTime,
      //trayName: this.currentResource?.collectionName ? this.currentResource?.collectionName : '',

      episodeNumber: ['webseries', 'webepisode'].includes(
        this.currentResource?.contentType || ''
      )
        ? this.currentResource?.episodeNumber?.toString() || '1'
        : undefined,
      seasonNumber: ['webseries', 'webepisode'].includes(
        this.currentResource?.contentType || ''
      )
        ? this.currentResource?.seasonNumber?.toString() || '1'
        : undefined,
      directorName: this.currentResource?.director[currentLang] || undefined,
      isFree: isFreeMovie(this.currentResource),
      showName: showName(this.currentResource, currentLang),
    };
    return attributes;
  }

  private skipIntroContent(): any {
    const currentLang = this.languageService.currLang;
    return {
      contentID: this.currentResource?.id,
      contentTitle: this.currentResource?.title[currentLang],
      networkType: this.currentResource?.network,
      showName: showName(this.currentResource, currentLang),
      ...this.getVODInfo(),
    };
  }

  private watchedContent(): any {
    const attributes = {
      ...this.getDefaultContentInfo(),
      networkType: this.currentResource?.network,
      source: 'TV',
    };

    if (this.getCastInfo(this.currentResource?.localizedCast)) {
      attributes.actorName = this.getCastInfo(
        this.currentResource?.localizedCast
      );
    }
    return attributes;
  }

  private addToWatchlistAndContentClicked(): any {
    const attributes = {
      ...this.getDefaultContentInfo(),
      ...this.getTrayContentData(),
      ...this.getVODInfo(),
      source: 'TV',
    };

    if (this.getCastInfo(this.currentResource?.localizedCast)) {
      attributes.actorName = this.getCastInfo(
        this.currentResource?.localizedCast
      );
    }
    return attributes;
  }

  private removeFromWatchlistContent(): any {
    const currentLang = this.languageService.currLang;
    const attributes = {
      ...this.getDefaultContentInfo(),
      itemPosition: this.currentResource?.trayPosition || undefined,
      episodeName: this.currentResource?.isSeries
        ? this.currentResource?.title[currentLang]
        : undefined,
      ...this.getVODInfo(),
      removeAll: undefined,
      source: 'TV',
    };

    if (this.getCastInfo(this.currentResource?.localizedCast)) {
      attributes.actorName = this.getCastInfo(
        this.currentResource?.localizedCast
      );
    }
    return attributes;
  }
  public getUserSubscriptionStatus(
    ahaUserType: string
  ): 'subscribed' | 'registered' {
    const subscribedTypes = [
      AhaUserType.TAMIL_SUBSCRIBED,
      AhaUserType.TELUGU_SUBSCRIBED,
      AhaUserType.PREMIUM,
    ];
    return subscribedTypes.includes(ahaUserType as AhaUserType)
      ? 'subscribed'
      : 'registered';
  }

  private clickedWatchOnRent(): any {
    const contentInfo = this.getDefaultContentInfo();
    const deviceID = localStorage.getItem(KEYS.DEVICE_REF) || undefined;
    const ahaUserType = this.sessionStorage.getItem(KEYS.AHA_USER_TYPE) || undefined;
    const userType = this.getUserSubscriptionStatus(ahaUserType);

    return {
      contentTitle: contentInfo?.contentTitle,
      contentGenre: contentInfo?.contentGenre,
      contentID: contentInfo?.contentID,
      platform: 'TV',
      deviceID,
      userType,
    };
  }

  public getVODInfo(): VODInfo {
    return {
      vodType: this.currentResource?.isTvod
        ? 'TVOD'
        : this.currentResource?.isPremium
          ? 'SVOD'
          : 'Free',
    };
  }
}
